<template>
  <b-modal :model-value="true">
    <ld-modal-content title="Report a Problem">
      <template #default>
        <ld-form without-content-wrapper>
          <template #default>
            <input-with-validation
              b-name="select"
              field-label="What problem did you see?"
              rules="required"
              v-model="form.reason"
              expanded
            >
              <option value="">Select a problem</option>
              <option v-for="reason in reasons" :key="reason.value" :value="reason.value">{{ reason.label }}</option>
            </input-with-validation>
            <input-with-validation v-if="affectedAreaOptions.length" field-label="What was affected?" v-model="form.affected_areas">
              <template #custom="{ handleChange }">
                <div class="is-flex is-flex-direction-column">
                  <div class="control" v-for="area in affectedAreaOptions" :key="area.value">
                    <b-checkbox v-model="form.affected_areas" :native-value="area.value" @update:modelValue="handleChange">
                      {{ area.label }}
                    </b-checkbox>
                  </div>
                </div>
              </template>
            </input-with-validation>
            <input-with-validation
              b-name="input"
              type="textarea"
              field-label="Other information"
              v-model="form.information"
              placeholder="Anything else that would help us investigate"
            />
          </template>
          <template #footer="{ meta }">
            <div class="is-flex is-justify-content-center" style="gap: 10px;">
              <button class="button is-text" @click="$emit('close')">Cancel</button>
              <button class="button is-primary" @click="submit" :disabled="!meta.valid || isSubmitting">Submit</button>
            </div>
          </template>
        </ld-form>
      </template>
    </ld-modal-content>
  </b-modal>
</template>

<script>
import LdModalContent from "@/components/LdModalContent.vue"
import InputWithValidation from "@/components/validation/InputWithValidation.vue"
import LdForm from "@/components/LdForm.vue"
import to from "@/lib/to"

const REASONS = [
  { value: 'video_quality', label: 'Video quality' },
  { value: 'white_video', label: 'White video' },
  { value: 'audio_problem', label: 'Audio problem' },
  { value: 'other', label: 'Other' }
]

const VIDEO_REASONS = ['video_quality', 'white_video']

const VIDEO_AREAS = [
  { value: 'my_view_of_patient', label: 'My view of the patient' },
  { value: 'my_view_of_self', label: 'My view of myself' },
  { value: 'patient_view_of_me', label: "The patient's view of me" }
]

const AUDIO_AREAS = [
  { value: 'cannot_hear_patient', label: "I can't hear the patient" },
  { value: 'patient_cannot_hear_me', label: "The patient can't hear me" }
]

export default {
  name: 'VideoIssueReportModal',
  components: {
    LdModalContent,
    InputWithValidation,
    LdForm
  },
  emits: ['close'],
  props: {
    videoDeviceId: {
      type: Number,
      required: true
    },
    patientMonitorId: {
      type: Number,
      default: null
    },
    meetingId: {
      type: Number,
      default: null
    },
    isVideoVisit: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    affectedAreaOptions() {
      if (this.form.reason === 'audio_problem') return AUDIO_AREAS
      if (VIDEO_REASONS.includes(this.form.reason) && (this.isVideoVisit || !!this.meetingId)) return VIDEO_AREAS
      return []
    }
  },
  watch: {
    'form.reason'() {
      this.form.affected_areas = []
    }
  },
  methods: {
    async submit() {
      if (this.isSubmitting) return
      this.isSubmitting = true

      const [err] = await to(this.$http.post('/video_issue_reports', {
        reason: this.form.reason,
        information: this.form.information,
        affected_areas: this.affectedAreaOptions.length ? this.form.affected_areas : [],
        video_device_id: this.videoDeviceId,
        patient_monitor_id: this.patientMonitorId,
        meeting_id: this.meetingId
      }, { skipLoading: true, skipDivisions: true }))

      this.isSubmitting = false

      if (err) {
        this.$buefy.toast.open({ message: 'Failed to submit report', type: 'is-danger', duration: 3000 })
        return
      }

      this.$buefy.toast.open({ message: 'Thank you for reporting this problem', type: 'is-success' })
      this.$emit('close')
    }
  },
  data() {
    return {
      reasons: REASONS,
      form: {
        reason: '',
        affected_areas: [],
        information: ''
      },
      isSubmitting: false
    }
  }
}
</script>
